// ======================================================================
// FILE:
// src/modules/service_crm/tickets/pages/ServiceTicketForm.js
//
// MODULE: Service CRM
// FEATURE: Create / Edit Service Ticket
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import FormSectionCard
from "../../components/FormSectionCard";

import serviceTicketService
from "../../services/serviceTicketService";

import installedProductService
from "../../services/installedProductService";

import {
  getAssignableBusinessPartners
}
from "../../../pms/services/businessPartnerService";


export default function ServiceTicketForm() {

  const navigate = useNavigate();

  const { id } = useParams();

  const isEdit = !!id;

  // -------------------------------------------------------------------
  // STATE
  // -------------------------------------------------------------------
  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [productRows, setProductRows] =
    useState([]);

  const [productSearch, setProductSearch] =
    useState("");
    const [bpList, setBpList] =
  useState([]);

  // -------------------------------------------------------------------
  // FORM
  // -------------------------------------------------------------------
  const [form, setForm] =
    useState({

      installed_product_id: "",

      sr_type: "Complaint",

      priority: "Medium",

      category: "",

      voc_details: "",

      complaint_details: "",

      assigned_engineer: "",
      assigned_business_partner_id: "",

      assigned_dealer: "",

      assigned_plant: "",
    });

  // -------------------------------------------------------------------
  // PRODUCT DETAILS
  // -------------------------------------------------------------------
  const [productInfo, setProductInfo] =
    useState(null);

  // -------------------------------------------------------------------
  // LOAD PRODUCTS
  // -------------------------------------------------------------------
  useEffect(() => {

    loadProducts();
     loadBusinessPartners();

    if (isEdit) {
      loadDetail();
    }

  }, []);

  // -------------------------------------------------------------------
  // LOAD PRODUCT LIST
  // -------------------------------------------------------------------
  async function loadProducts() {

    try {

      const res =
        await installedProductService
          .listInstalledProducts({

            per_page: 100,
          });

      setProductRows(
        res.items || []
      );

    } catch (err) {

      console.error(err);
    }
  }

  // -------------------------------------------------------------------
  // LOAD BUSINESS PARTNERS
  // -------------------------------------------------------------------
  async function loadBusinessPartners() {

    try {

      const res =
        await getAssignableBusinessPartners();

      const serviceProviders =

        (res.data.data || [])

        .filter(
          bp =>
            bp.role ===
            "SERVICE_PROVIDER"
        );

      setBpList(
        serviceProviders
      );

    } catch (err) {

      console.error(
        "Failed to load Service Providers",
        err
      );

    }

  }

  // -------------------------------------------------------------------
  // LOAD DETAIL
  // -------------------------------------------------------------------
  async function loadDetail() {

    try {

      setLoading(true);

      const res =
        await serviceTicketService
          .getTicket(id);

      setForm({

        installed_product_id:
          res.installed_product_id || "",

        sr_type:
          res.sr_type || "",

        priority:
          res.priority || "",

        category:
          res.category || "",

        voc_details:
          res.voc_details || "",

        complaint_details:
          res.complaint_details || "",

        assigned_engineer:
          res.assigned_engineer || "",

        assigned_dealer:
          res.assigned_dealer || "",

        assigned_plant:
          res.assigned_plant || "",
      });

      setProductInfo(res);

    } catch (err) {

      console.error(err);

      alert(
        "Failed to load ticket"
      );

    } finally {

      setLoading(false);
    }
  }

  // -------------------------------------------------------------------
  // CHANGE
  // -------------------------------------------------------------------
  function handleChange(e) {

    const {
      name,
      value,
    } = e.target;

    // ---------------------------------------------------------
    // Service Provider
    // ---------------------------------------------------------
    if (name === "assigned_engineer") {

      const bp =
        bpList.find(
          x => x.name === value
        );

      setForm({

        ...form,

        assigned_engineer: value,

        assigned_business_partner_id:
          bp
            ? bp.id
            : "",
      });

      return;
    }

    setForm({

      ...form,

      [name]: value,
    });
}

  // -------------------------------------------------------------------
  // PRODUCT SELECT
  // -------------------------------------------------------------------
  function handleProductSelect(item) {

  let row = null;
  let value = null;

  // Called from dropdown
  if (item.target) {
    value = item.target.value;

    row = productRows.find(
      p => String(p.id) === String(value)
    );
  }
  // Called from table row
  else {
    row = item;
    value = item.id;
  }

  setForm({
  ...form,
  //  ...prev,
    installed_product_id: value,
  });

  setProductInfo(row || null);
}
  // -------------------------------------------------------------------
  // FILTER PRODUCTS
  // -------------------------------------------------------------------
  const filteredProducts = productRows.filter((p) => {

    if (!productSearch.trim()) {
      return false;
    }

    const search = productSearch.toLowerCase();

    return (
      (p.instance_id || "")
        .toLowerCase()
        .includes(search) ||

      (p.engine_app_code || "")
        .toLowerCase()
        .includes(search) ||

      (p.canopy_number || "")
        .toLowerCase()
        .includes(search) ||

      (p.engine_serial_no || "")
        .toLowerCase()
        .includes(search) ||

      (p.customer_name || "")
        .toLowerCase()
        .includes(search)
    );

  });
  
  // -------------------------------------------------------------------
  // SAVE
  // -------------------------------------------------------------------
  async function handleSubmit(e) {

    e.preventDefault();

    try {

      setSaving(true);

      if (
        !form.installed_product_id
      ) {

        alert(
          "Please select installed product"
        );

        return;
      }

      if (!form.voc_details) {

        alert(
          "Please enter complaint"
        );

        return;
      }

      // ---------------------------------------------------------------
      // CREATE
      // ---------------------------------------------------------------
      if (!isEdit) {

        await serviceTicketService
          .createTicket(form);

        alert(
          "Ticket created successfully"
        );
      }

      // ---------------------------------------------------------------
      // UPDATE
      // ---------------------------------------------------------------
      else {

        await serviceTicketService
          .updateTicket(
            id,
            form
          );

        alert(
          "Ticket updated successfully"
        );
      }

      navigate(
        "/service-crm/tickets"
      );

    } catch (err) {

      console.error(err);

      alert(
        "Failed to save ticket"
      );

    } finally {

      setSaving(false);
    }
  }

  // -------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------
  return (

    <form
      onSubmit={handleSubmit}
    >

      {/* ========================================================= */}
      {/* PAGE HEADER */}
      {/* ========================================================= */}

      <div
        style={{

          display: "flex",

          justifyContent:
            "space-between",

          alignItems: "center",

          marginBottom: 20,
        }}
      >

        <div>

          <h2
            style={{
              margin: 0,
            }}
          >

            {isEdit
              ? "Edit Ticket"
              : "Create Service Ticket"}

          </h2>

          <div
            style={{
              color: "#6c757d",
            }}
          >
            Manage service request
          </div>

        </div>

      </div>

      {/* ========================================================= */}
      {/* BASIC INFO */}
      {/* ========================================================= */}

      <FormSectionCard
        title="Basic Information"
      >

        <div
          className="row"
        >

          <div
            className="col-md-4 mb-3"
          >

            <label>
              SR Type
            </label>

            <select
              className="form-select"

              name="sr_type"

              value={form.sr_type}

              onChange={
                handleChange
              }
            >

              <option>
                Complaint
              </option>

              <option>
                Service
              </option>

              <option>
                Breakdown
              </option>

            </select>

          </div>

          <div
            className="col-md-4 mb-3"
          >

            <label>
              Priority
            </label>

            <select
              className="form-select"

              name="priority"

              value={form.priority}

              onChange={
                handleChange
              }
            >

              <option>
                Low
              </option>

              <option>
                Medium
              </option>

              <option>
                High
              </option>

            </select>

          </div>

          <div
            className="col-md-4 mb-3"
          >

            <label>
              Category
            </label>

            <input
              type="text"

              className="form-control"

              name="category"

              value={form.category}

              onChange={
                handleChange
              }
            />

          </div>

        </div>

      </FormSectionCard>

      {/* ========================================================= */}
      {/* PRODUCT */}
      {/* ========================================================= */}

      <FormSectionCard
        title="Installed Product"
      >

        <div
          className="row"
        >

          <div
            className="col-md-12 mb-3"
          >

            <label>
              Search Installed Product
            </label>

            <input
              type="text"
              className="form-control mb-3"
              placeholder="Search by Instance ID / App Code / Canopy No / Engine Serial No / Customer Name"
              value={productSearch}
              onChange={(e) =>
                setProductSearch(e.target.value)
              }
            />
            <h6 className="mt-3 mb-2">Matching Products</h6>
            <div className="table-responsive mb-3">
            <table className="table table-bordered table-hover table-sm">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: "60px" }}>#</th>
                        <th>Instance ID</th>
                        <th>App Code</th>
                        <th>Customer</th>
                        <th>Engine Serial No.</th>
                        <th>Canopy No.</th>
                    </tr>
                </thead>
                <tbody>
                  {filteredProducts.length === 0 ? (
                      <tr>
                          <td colSpan="6" className="text-center text-muted">
                              No matching products found
                          </td>
                      </tr>
                  ) : (
                      filteredProducts.map((p, index) => (
                            <tr
                              key={p.id || index}
                              style={{
                                  cursor: "pointer",
                                  backgroundColor:
                                  Number(form.installed_product_id) === Number(p.id)
                                      ? "#d1ecf1"
                                      : "transparent"
                              }}
                              onClick={() => handleProductSelect(p)}
                          >  
                              <td>{index + 1}</td>
                              <td>{p.instance_id}</td>
                              <td>{p.engine_app_code}</td>
                              <td>{p.customer_name}</td>
                              <td>{p.engine_serial_no}</td>
                              <td>{p.canopy_number}</td>
                          </tr>
                      ))
                  )}
              </tbody>
            </table>
        </div>

            <label>
              Select Installed Product
            </label>

            <select
              className="form-select"

              value={
                form.installed_product_id
              }

              onChange={
                handleProductSelect
              }
            >

              <option value="">
                Select Product
              </option>

              {filteredProducts.map((p) => (

                <option
                  key={p.id}
                  value={p.id}
                >

                  {p.instance_id}
                  {" | "}
                  {p.engine_app_code}
                  {" | "}
                  {p.canopy_number}
                  {" | "}
                  {p.engine_serial_no}
                  {" | "}
                  {p.customer_name}

                </option>
              ))}

            </select>

          </div>

        </div>

        {/* -------------------------------------------------------- */}
        {/* PRODUCT INFO */}
        {/* -------------------------------------------------------- */}

        {productInfo && (

          <div
            className="row"
          >

            <div
              className="col-md-3 mb-3"
            >

              <label>
                Instance ID
              </label>

              <input
                className="form-control"
                readOnly
                value={
                  productInfo.instance_id || ""
                }
              />

            </div>

            <div
              className="col-md-3 mb-3"
            >

              <label>
                App Code
              </label>

              <input
                className="form-control"
                readOnly
                value={
                  productInfo.engine_app_code || ""
                }
              />

            </div>

            <div
              className="col-md-3 mb-3"
            >

              <label>
                Serial No
              </label>

              <input
                className="form-control"
                readOnly
                value={
                  productInfo.engine_serial_no || ""
                }
              />

            </div>

            <div
              className="col-md-3 mb-3"
            >

              <label>
                Customer
              </label>

              <input
                className="form-control"
                readOnly
                value={
                  productInfo.customer_name || ""
                }
              />

            </div>

          </div>
        )}

      </FormSectionCard>

      {/* ========================================================= */}
      {/* COMPLAINT */}
      {/* ========================================================= */}

      <FormSectionCard
        title="Complaint Details"
      >

        <div
          className="row"
        >

          <div
            className="col-md-12 mb-3"
          >

            <label>
              VOC / Complaint
            </label>

            <textarea
              rows="4"

              className="form-control"

              name="voc_details"

              value={
                form.voc_details
              }

              onChange={
                handleChange
              }
            />

          </div>

          <div
            className="col-md-12 mb-3"
          >

            <label>
              Additional Notes
            </label>

            <textarea
              rows="3"

              className="form-control"

              name="complaint_details"

              value={
                form.complaint_details
              }

              onChange={
                handleChange
              }
            />

          </div>

        </div>

      </FormSectionCard>

      {/* ========================================================= */}
      {/* ASSIGNMENT */}
      {/* ========================================================= */}

      <FormSectionCard
        title="Assignment"
      >

        <div
          className="row"
        >

          <div
            className="col-md-4 mb-3"
          >

            <label>
              Assigned Service Provider
            </label>

            <select

              className="form-select"

              name="assigned_engineer"

              value={
                form.assigned_engineer
              }

              onChange={
                handleChange
              }
            >

              <option value="">
                Select Service Provider
              </option>

              {bpList.map(bp => (

                <option
                  key={bp.id}
                  value={bp.name}
                >

                  {bp.name}
                  {" "}
                  ({bp.role})

                </option>

              ))}

            </select>

          </div>

          <div
            className="col-md-4 mb-3"
          >

            <label>
              Dealer
            </label>

            <input
              type="text"

              className="form-control"

              name="assigned_dealer"

              value={
                form.assigned_dealer
              }

              onChange={
                handleChange
              }
            />

          </div>

          <div
            className="col-md-4 mb-3"
          >

            <label>
              Plant
            </label>

            <input
              type="text"

              className="form-control"

              name="assigned_plant"

              value={
                form.assigned_plant
              }

              onChange={
                handleChange
              }
            />

          </div>

        </div>

      </FormSectionCard>

      {/* ========================================================= */}
      {/* ACTIONS */}
      {/* ========================================================= */}

      <div
        style={{

          display: "flex",

          gap: 12,

          justifyContent: "end",

          marginBottom: 40,
        }}
      >

        <button
          type="button"

          className="btn btn-secondary"

          onClick={() =>
            navigate(
              "/service-crm/tickets"
            )
          }
        >
          Cancel
        </button>

        <button
          type="submit"

          className="btn btn-primary"

          disabled={saving}
        >

          {saving
            ? "Saving..."
            : (
              isEdit
                ? "Update Ticket"
                : "Create Ticket"
            )}

        </button>

      </div>

    </form>
  );
}